'use client';
import React, { useState } from 'react';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface Integration {
  id: string;
  name: string;
  description: string;
  category: string;
  status: 'connected' | 'disconnected' | 'error';
  lastSync: string | null;
  recordsSync: string | null;
  color: string;
  initials: string;
}

const integrations: Integration[] = [
  {
    id: 'int-salesforce',
    name: 'Salesforce CRM',
    description: 'Sync pipeline, opportunities, and account data for sales analytics',
    category: 'CRM',
    status: 'connected',
    lastSync: '9 minutes ago',
    recordsSync: '48,291 records',
    color: 'bg-blue-100 text-blue-600',
    initials: 'SF',
  },
  {
    id: 'int-stripe',
    name: 'Stripe Payments',
    description: 'Pull revenue, subscription, and churn data from Stripe billing',
    category: 'Payments',
    status: 'connected',
    lastSync: '4 minutes ago',
    recordsSync: '$4.28M synced',
    color: 'bg-purple-100 text-purple-600',
    initials: 'ST',
  },
  {
    id: 'int-zendesk',
    name: 'Zendesk Support',
    description: 'Import ticket volume, resolution times, and CSAT scores',
    category: 'Support',
    status: 'error',
    lastSync: '2 hours ago',
    recordsSync: 'Sync failed',
    color: 'bg-orange-100 text-orange-600',
    initials: 'ZD',
  },
  {
    id: 'int-ga4',
    name: 'Google Analytics 4',
    description: 'Track active users, session data, and conversion events',
    category: 'Analytics',
    status: 'connected',
    lastSync: '18 minutes ago',
    recordsSync: '84,291 sessions',
    color: 'bg-yellow-100 text-yellow-600',
    initials: 'GA',
  },
  {
    id: 'int-hubspot',
    name: 'HubSpot Marketing',
    description: 'Sync lead and campaign performance data for funnel analysis',
    category: 'Marketing',
    status: 'disconnected',
    lastSync: null,
    recordsSync: null,
    color: 'bg-orange-100 text-orange-500',
    initials: 'HS',
  },
  {
    id: 'int-slack',
    name: 'Slack',
    description: 'Deliver report summaries and critical alerts to Slack channels',
    category: 'Notifications',
    status: 'connected',
    lastSync: 'Active',
    recordsSync: '3 channels connected',
    color: 'bg-pink-100 text-pink-600',
    initials: 'SL',
  },
];

const statusConfig = {
  connected: { label: 'Connected', icon: CheckCircle, class: 'text-positive', bg: 'bg-positive/10 text-positive' },
  disconnected: { label: 'Disconnected', icon: XCircle, class: 'text-muted-foreground', bg: 'bg-muted text-muted-foreground' },
  error: { label: 'Sync Error', icon: XCircle, class: 'text-negative', bg: 'bg-negative/10 text-negative' },
};

export default function IntegrationSettings() {
  const [intgs, setIntgs] = useState<Integration[]>(integrations);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const handleSync = (id: string, name: string) => {
    setSyncingId(id);
    // Backend integration point: POST /api/integrations/:id/sync
    setTimeout(() => {
      setSyncingId(null);
      setIntgs((prev) =>
        prev.map((i) => i.id === id ? { ...i, lastSync: 'just now', status: 'connected' } : i)
      );
      toast.success(`${name} synced successfully`);
    }, 1800);
  };

  const handleToggle = (id: string, name: string, currentStatus: Integration['status']) => {
    if (currentStatus === 'connected' || currentStatus === 'error') {
      setIntgs((prev) =>
        prev.map((i) => i.id === id ? { ...i, status: 'disconnected', lastSync: null, recordsSync: null } : i)
      );
      toast.success(`${name} disconnected`);
    } else {
      // Backend integration point: POST /api/integrations/:id/connect
      toast.info(`Connecting ${name} — OAuth flow would open here`);
    }
  };

  return (
    <div className="space-y-5">
      <div className="card-surface p-6">
        <h2 className="text-base font-semibold text-foreground mb-1">Connected Data Sources</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Manage the data sources that power your NexusMetrics dashboard
        </p>

        <div className="space-y-3">
          {intgs.map((intg) => {
            const status = statusConfig[intg.status];
            const StatusIcon = status.icon;
            const isConnected = intg.status === 'connected';
            const isError = intg.status === 'error';
            return (
              <div
                key={intg.id}
                className={`rounded-xl border p-4 transition-all duration-150 ${
                  isError ? 'border-negative/30 bg-negative/5' : 'border-border hover:bg-muted/30'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0 ${intg.color}`}>
                      {intg.initials}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-foreground">{intg.name}</p>
                        <span className="text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                          {intg.category}
                        </span>
                        <span className={`flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 ${status.bg}`}>
                          <StatusIcon size={11} />
                          {status.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{intg.description}</p>
                      {intg.lastSync && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Last sync: <span className="font-medium text-foreground">{intg.lastSync}</span>
                          {intg.recordsSync && (
                            <span className="ml-2 text-muted-foreground">· {intg.recordsSync}</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(isConnected || isError) && (
                      <button
                        onClick={() => handleSync(intg.id, intg.name)}
                        disabled={syncingId === intg.id}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors btn-press disabled:opacity-50"
                        aria-label={`Sync ${intg.name}`}
                      >
                        <RefreshCw
                          size={13}
                          className={syncingId === intg.id ? 'animate-spin' : ''}
                        />
                      </button>
                    )}
                    <button
                      onClick={() => handleToggle(intg.id, intg.name, intg.status)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors btn-press ${
                        isConnected || isError
                          ? 'bg-muted text-muted-foreground hover:bg-negative/10 hover:text-negative border border-border'
                          : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      }`}
                    >
                      {isConnected || isError ? 'Disconnect' : 'Connect'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}